/* Matemática do storyboard da meia-entrada com documento comprobatório.
   Fica fora do componente para o fast-refresh não quebrar e para os
   invariantes serem testáveis sem montar React. */

/* ── Colunas (os três atores) ── */
export const COLS = [
  { x: 14, w: 158, title: "Produtor" },
  { x: 180, w: 158, title: "Comprador" },
  { x: 346, w: 160, title: "Portaria" },
];
export const colCenter = (i: number) => COLS[i].x + COLS[i].w / 2;

/* ── Alvos do cursor ── */
export const SWITCH_MEIA = { x: 150, y: 104 };
export const SWITCH_DOC = { x: 150, y: 146 };
export const TIER_CARD = { x: colCenter(1), y: 100 };
export const DROPZONE = { x: colCenter(1), y: 164 };
export const CONFIRM_BTN = { x: colCenter(1), y: 212 };
export const QR_CENTER = { x: colCenter(2), y: 104 };
export const DOC_LANDING = { x: colCenter(2), y: 198 };
export const IDLE = { x: 40, y: 250 };

/* ── Roteiro ── */
export type Beat =
  | { k: "toggleMeia" }
  | { k: "toggleDoc" }
  | { k: "pickTier" }
  | { k: "upload" }
  | { k: "confirm" }
  | { k: "scan" };

export const BEATS: Beat[] = [
  { k: "toggleMeia" },
  { k: "toggleDoc" },
  { k: "pickTier" },
  { k: "upload" },
  { k: "confirm" },
  { k: "scan" },
];

/** Qual coluna está em foco em cada batida. */
export const BEAT_COL: Record<Beat["k"], number> = {
  toggleMeia: 0,
  toggleDoc: 0,
  pickTier: 1,
  upload: 1,
  confirm: 1,
  scan: 2,
};

const CL = { reach: 620, press: 180, settle: 240, gap: 220 };
/** Folga extra depois de batidas que produzem algo para ler. */
const PAD: Partial<Record<Beat["k"], number>> = {
  toggleDoc: 380,
  upload: 1500, // envio do arquivo acontece aqui
  confirm: 420,
  scan: 2600, // leitura do QR, conferência e liberação
};

/* O tempo de percurso acompanha a distância: um pulo longo entre colunas leva
   mais que um ajuste de dois pixels. Sem isso o easeInOut, cuja velocidade de
   pico é 3× a média, joga o cursor a ~800px/s nos trechos longos. */
const REACH_REF = 150;
const REACH_MIN = 420;
const REACH_MAX = 1050;
const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(b.x - a.x, b.y - a.y);
const clampN = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Percurso da batida `i`, considerando de onde o cursor vem. */
export const reachAt = (i: number) => {
  const from = i === 0 ? IDLE : beatTarget(BEATS[i - 1]);
  const d = dist(from, beatTarget(BEATS[i]));
  return clampN((CL.reach * d) / REACH_REF, REACH_MIN, REACH_MAX);
};

export const beatDur = (i: number) => reachAt(i) + CL.press + CL.settle + CL.gap + (PAD[BEATS[i].k] ?? 0);

export const STARTS: number[] = [];
let acc = 0;
for (let i = 0; i < BEATS.length; i++) {
  STARTS.push(acc);
  acc += beatDur(i);
}
export const STORY_END = acc;
export const HOLD = 1500;
export const RESET = 700;
export const CYCLE = STORY_END + HOLD + RESET;
const FADE_IN = 420;

const UP_IDX = BEATS.findIndex((b) => b.k === "upload");
const SCAN_IDX = BEATS.findIndex((b) => b.k === "scan");

/* ── Tempos internos da batida de upload (relativos ao início dela) ── */
const UP_CLICK = reachAt(UP_IDX) + CL.press;
const UP_FILE_IN = UP_CLICK + 200; // arquivo entra
const UP_BAR = UP_FILE_IN + 250; // barra começa
const UP_BAR_END = UP_BAR + 850; // barra completa → check

/* ── Tempos internos da batida do scan ── */
const SCAN_QR = reachAt(SCAN_IDX) + CL.press + 200;
const SCAN_BADGE = SCAN_QR + 500;
const SCAN_DOC_FLY = SCAN_BADGE + 500;
const SCAN_DOC_LAND = SCAN_DOC_FLY + 800;
const SCAN_OK = SCAN_DOC_LAND + 600;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpVec = (a: { x: number; y: number }, b: { x: number; y: number }, t: number) => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
});
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const smoothstep = (t: number) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};
const bump = (t: number) => (t < 0 || t > 1 ? 0 : Math.sin(t * Math.PI));

export function beatTarget(b: Beat) {
  switch (b.k) {
    case "toggleMeia":
      return SWITCH_MEIA;
    case "toggleDoc":
      return SWITCH_DOC;
    case "pickTier":
      return TIER_CARD;
    case "upload":
      return DROPZONE;
    case "confirm":
      return CONFIRM_BTN;
    case "scan":
      return QR_CENTER;
  }
}

export interface Frame {
  cursor: { x: number; y: number };
  press: number;
  clickPulse: number;
  showCursor: boolean;
  /** estado do painel do produtor */
  meiaOn: boolean;
  docOn: boolean;
  docRowIn: number; // 0..1 — a segunda linha só existe depois da primeira
  /** estado do checkout */
  tierPicked: boolean;
  stepIn: number; // 0..1 — surgimento da etapa extra no fluxo
  fileIn: number; // 0..1 — entrada do arquivo na área de upload
  uploadProgress: number; // 0..1
  uploadDone: boolean;
  confirmOn: boolean;
  /** estado da portaria */
  qrPulse: number;
  badgeIn: number;
  docFly: number; // 0..1 — documento indo do checkout para a portaria
  accessOk: number;
  /** foco entre colunas e fade do ciclo */
  focus: number;
  stage: number;
  beat: number;
}

export function computeFrame(e: number): Frame {
  const stage = Math.min(smoothstep(e / FADE_IN), e > CYCLE - RESET ? smoothstep((CYCLE - e) / RESET) : 1);

  let index = BEATS.length - 1;
  let local = 0;
  if (e < STORY_END) {
    let i = 0;
    while (i < BEATS.length - 1 && e >= STARTS[i + 1]) i++;
    index = i;
    local = e - STARTS[i];
  } else {
    local = beatDur(index);
  }
  const reach = reachAt(index);

  const done = (k: Beat["k"]) => {
    const i = BEATS.findIndex((b) => b.k === k);
    // o efeito acontece no meio do clique, não no fim da batida
    return e >= STARTS[i] + reachAt(i) + CL.press * 0.6;
  };

  // cursor
  const b = BEATS[index];
  const target = beatTarget(b);
  const from = index === 0 ? IDLE : beatTarget(BEATS[index - 1]);
  let cursor = { ...target };
  let press = 0;
  let clickPulse = 0;

  if (e < STORY_END) {
    if (local < reach) {
      cursor = lerpVec(from, target, easeInOut(local / reach));
    } else if (local < reach + CL.press) {
      const t = (local - reach) / CL.press;
      press = easeOut(t);
      clickPulse = t;
    } else if (local < reach + CL.press + CL.settle) {
      press = 1 - easeOut((local - reach - CL.press) / CL.settle);
    }
  } else if (e < STORY_END + HOLD) {
    cursor = { ...beatTarget(BEATS[BEATS.length - 1]) };
  } else {
    cursor = lerpVec(beatTarget(BEATS[BEATS.length - 1]), IDLE, easeInOut((e - STORY_END - HOLD) / RESET));
  }

  // upload
  const upIdx = BEATS.findIndex((x) => x.k === "upload");
  const upLocal = e - STARTS[upIdx];
  const fileIn = smoothstep((upLocal - UP_FILE_IN) / 320);
  const uploadProgress = clamp01((upLocal - UP_BAR) / (UP_BAR_END - UP_BAR));
  const uploadDone = upLocal >= UP_BAR_END;

  // scan
  const scanIdx = BEATS.findIndex((x) => x.k === "scan");
  const scanLocal = e - STARTS[scanIdx];
  const qrPulse = scanLocal >= SCAN_QR ? bump(clamp01((scanLocal - SCAN_QR) / 700)) : 0;
  const badgeIn = smoothstep((scanLocal - SCAN_BADGE) / 400);
  const docFly = smoothstep((scanLocal - SCAN_DOC_FLY) / (SCAN_DOC_LAND - SCAN_DOC_FLY));
  const accessOk = smoothstep((scanLocal - SCAN_OK) / 450);

  const focus = BEAT_COL[b.k];

  return {
    cursor,
    press,
    clickPulse,
    showCursor: e < STORY_END + HOLD,
    meiaOn: done("toggleMeia"),
    docOn: done("toggleDoc"),
    docRowIn: smoothstep((e - (STARTS[1] - 260)) / 320),
    tierPicked: done("pickTier"),
    stepIn: smoothstep((e - (STARTS[3] - 300)) / 340),
    fileIn,
    uploadProgress,
    uploadDone,
    confirmOn: done("confirm"),
    qrPulse,
    badgeIn,
    docFly,
    accessOk,
    focus,
    stage,
    beat: index,
  };
}
